import { startOfMonth, endOfMonth, subMonths, format } from 'date-fns';
import { db, Row } from './db';
import { toCents, fromCents } from '../utils/money';
import { calendarDaysUntil, formatCalendarDate } from '../utils/dateUtils';
import { isLowStockItem, lowStockThresholdOf } from '../utils/lowStock';

/** PaginatedResponse shape matching backend list endpoints / src/utils/api.ts. */
export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function readHorse(id: string): Promise<Row | undefined> {
  return db.horses.get(id);
}

/** Generic "rows for a horse" — used by the chart's child-record sections. */
export async function readByHorse(table: string, horseId: string): Promise<Row[]> {
  return (db as any)[table].where('horseId').equals(horseId).toArray();
}

/** readByHorse minus archived rows: the offline twin of a list route that hides archived by default. */
export async function readActiveByHorse(table: string, horseId: string): Promise<Row[]> {
  return (await readByHorse(table, horseId)).filter((r) => !r.isArchived);
}

export interface HorsePageParams {
  page: number;
  limit?: number;
  search?: string;
  status?: string;
  location?: string;
}

/** Local equivalent of horseApi.listPaginated — filter/sort/paginate in memory. */
export async function readHorsesPage(p: HorsePageParams): Promise<Paginated<Row>> {
  let rows = await db.horses.toArray();
  const search = p.search?.trim(); // backend guards on search.trim().length > 0
  if (search) {
    // Mirror buildHorseListWhere field for field (it is the online half of the
    // same search box), including owner name/email/phone.
    const q = search.toLowerCase();
    const hit = (v: unknown) => `${v ?? ''}`.toLowerCase().includes(q);
    rows = rows.filter(
      (h) =>
        hit(h.name) ||
        hit(h.registeredName) ||
        hit(h.microchipId) ||
        hit(h.peiNumber) ||
        hit(h.patientId) ||
        (Array.isArray(h.owners) &&
          h.owners.some((o: any) => o && (hit(o.name) || hit(o.email) || hit(o.phone)))),
    );
  }
  if (p.status && p.status !== 'ALL') rows = rows.filter((h) => h.status === p.status);
  if (p.location) rows = rows.filter((h) => h.location === p.location);
  rows.sort((a, b) => `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`));
  return paginate(rows, p.page, p.limit ?? 50);
}

/** Local equivalent of billingApi.listInvoicesPaginated({ horseId }). */
export async function readInvoicesPageByHorse(horseId: string, page = 1, limit = 50): Promise<Paginated<Row>> {
  const rows = await readByHorse('invoices', horseId);
  return paginate(rows, page, limit);
}

/**
 * Local equivalent of appointmentApi.listPaginated({ dateFrom, dateTo }) for the
 * calendar's offline read. Best-effort date-range filter over the replica, then
 * hydrate each row's nested `horse` + `assignedVet` to match the online REST
 * `include` shape — the /sync feed stores scalar-only appointment rows, but the
 * calendar renderer hard-derefs `appointment.horse.name`/`.medicalAlerts`, so a
 * bare row would crash the daily/weekly view. Joining from db.horses/db.users
 * here also covers the optimistic offline-create row (it flows through this same
 * reader), so a just-booked offline appointment renders immediately.
 */
export async function readAppointmentsInRange(params: {
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}): Promise<Paginated<Row>> {
  const from = params.dateFrom ? new Date(params.dateFrom).getTime() : -Infinity;
  const to = params.dateTo ? new Date(params.dateTo).getTime() : Infinity;
  let rows = await db.appointments.toArray();
  rows = rows.filter((a) => {
    const t = new Date(a.date as string).getTime();
    return !Number.isNaN(t) && t >= from && t <= to;
  });
  rows.sort((a, b) => `${a.date ?? ''}`.localeCompare(`${b.date ?? ''}`));
  const page = paginate(rows, params.page ?? 1, params.limit ?? 1000);
  const hydrated = await Promise.all(
    page.data.map(async (a) => ({
      ...a,
      horse: a.horseId ? await db.horses.get(a.horseId as string) : undefined,
      // Owner-subject bookings: hydrate the client so the calendar block can
      // show the owner's name. (Barns are not in the replica — for barn-level
      // bookings the `location` string carries the barn name, server-set.)
      client: a.clientId ? await db.clients.get(a.clientId as string) : undefined,
      assignedVet: a.assignedVetId ? await db.users.get(a.assignedVetId as string) : undefined,
    })),
  );
  return { ...page, data: hydrated };
}

/**
 * Generic "rows for a client" — the client-scoped tables (clientNotes) are
 * indexed by clinicId only, so filter by clientId in memory (low volume per
 * client). Newest-first to match the REST `orderBy: createdAt desc`.
 */
export async function readByClient(table: string, clientId: string): Promise<Row[]> {
  const rows = (await (db as any)[table].toArray()) as Row[];
  return rows
    .filter((r) => r.clientId === clientId)
    .sort((a, b) => `${b.createdAt ?? ''}`.localeCompare(`${a.createdAt ?? ''}`));
}

export interface ClientPageParams {
  page: number;
  limit?: number;
  search?: string;
  status?: string;
}

/**
 * The /sync clients pull stores RAW Client rows — without the REST list's
 * server-COMPUTED `currentBalance`/`horseCount`/`horses`. Default them so the
 * offline list/detail never renders `undefined` (e.g. `formatDollars(undefined)`)
 * or a misleading 0-vs-blank. (Read-side, so it stays inert online.)
 */
function withClientReadDefaults(c: Row, balances: Map<string, number>): Row {
  return {
    ...c,
    currentBalance: balances.get(`${c.id}`) ?? 0,
    horseCount: c.horseCount ?? (Array.isArray(c.horses) ? c.horses.length : 0),
    horses: Array.isArray(c.horses) ? c.horses : [],
  };
}

/**
 * What each client owes on invoices billed to them, from the replica (review fix 39). The pulled
 * `currentBalance` is a dead column that always read 0, so every client showed $0.00 offline; a
 * replica row pulled before it left the pull still carries it, so it is never read. This is the
 * server's DIRECT leg (`Invoice.clientId`, open statuses, net of succeeded and processing payments,
 * clamped per invoice). Its legacy leg, a clientId-null invoice shared across a horse's owners,
 * needs owner links the replica does not hold, so those invoices are not counted offline.
 */
async function readClientBalances(clientIds: string[]): Promise<Map<string, number>> {
  const wanted = new Set(clientIds);
  const open = OPEN_INVOICE_STATUSES as readonly string[];
  const invoices = (await db.invoices.toArray()).filter(
    (i) => wanted.has(`${i.clientId}`) && open.includes(`${i.status}`),
  );
  const ids = new Set(invoices.map((i) => `${i.id}`));
  const paidCentsBy = new Map<string, number>();
  for (const p of await db.payments.toArray()) {
    if (!ids.has(`${p.invoiceId}`) || (p.status !== 'succeeded' && p.status !== 'processing')) continue;
    paidCentsBy.set(`${p.invoiceId}`, (paidCentsBy.get(`${p.invoiceId}`) ?? 0) + (typeof p.amount === 'number' ? p.amount : 0));
  }
  const centsBy = new Map<string, number>();
  for (const i of invoices) {
    const owed = Math.max(0, toCents(invoiceTotal(i)) - (paidCentsBy.get(`${i.id}`) ?? 0));
    centsBy.set(`${i.clientId}`, (centsBy.get(`${i.clientId}`) ?? 0) + owed);
  }
  return new Map(Array.from(centsBy, ([id, cents]) => [id, fromCents(cents)]));
}

/** Local equivalent of clientApi.listPaginated — filter/sort/paginate in memory. */
export async function readClientsPage(p: ClientPageParams): Promise<Paginated<Row>> {
  let rows = await db.clients.toArray();
  if (p.search) {
    const q = p.search.toLowerCase();
    rows = rows.filter(
      (c) =>
        `${c.name ?? ''}`.toLowerCase().includes(q) ||
        `${c.email ?? ''}`.toLowerCase().includes(q) ||
        `${c.clientNumber ?? ''}`.toLowerCase().includes(q),
    );
  }
  if (p.status && p.status !== 'ALL') rows = rows.filter((c) => c.status === p.status);
  rows.sort((a, b) => `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`));
  const page = paginate(rows, p.page, p.limit ?? 50);
  const balances = await readClientBalances(page.data.map((c) => `${c.id}`));
  page.data = page.data.map((c) => withClientReadDefaults(c, balances));
  return page;
}

export async function readClient(id: string): Promise<Row | undefined> {
  const row = await db.clients.get(id);
  return row ? withClientReadDefaults(row, await readClientBalances([id])) : row;
}

export async function readActiveClientsCount(): Promise<number> {
  return (await db.clients.toArray()).filter((c) => c.status === 'ACTIVE').length;
}

/**
 * Vets/owners assignable to appointments — mirrors users.service
 * getVeterinariansByClinic (role VETERINARIAN/OWNER, active, name-sorted) over the
 * synced users replica. Used by the appointment dialog's vet picker AND the
 * calendar's provider columns (which gate the per-vet Block-Time flow), so the
 * whole scheduling surface works offline.
 */
export async function readVeterinarians(): Promise<Row[]> {
  const rows = await db.users.toArray();
  return rows
    .filter((u) => ['VETERINARIAN', 'OWNER'].includes(`${u.role}`) && u.active !== false)
    .sort((a, b) => `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`));
}

/** SOAP templates for the templated-SOAP picker (synced replica; name-sorted). */
export async function readSoapTemplates(): Promise<Row[]> {
  return (await db.soapTemplates.toArray()).sort(
    (a, b) => `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`),
  );
}

/** The single synced clinic row (offline invoice letterhead / clinic header). */
export async function readClinic(): Promise<Row | undefined> {
  return (await db.clinic.toArray())[0];
}

/**
 * Service catalog for offline pickers/line items — active rows only (clinic +
 * global null-clinicId pricing), optional category filter, name-sorted. Mirrors
 * GET /service-catalog with isActive=true defaults.
 */
export async function readServiceCatalog(params: { category?: string } = {}): Promise<Row[]> {
  // Mirror REST findAll EXACTLY: clinic-owned rows ONLY. The replica also holds
  // the global template rows (clinicId=null — the sync pull includes them so
  // id-references resolve offline), but the REST list endpoint never returns
  // them: a clinic seeded via bulk-import owns COPIES of the templates, so
  // listing both showed every appointment type twice (staging bug 2026-06-10).
  let rows = (await db.serviceCatalog.toArray()).filter(
    (i) => i.clinicId != null && i.isActive !== false,
  );
  if (params.category) rows = rows.filter((i) => i.category === params.category);
  return rows.sort((a, b) => `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`));
}

/**
 * Offline equivalent of GET /payments/invoice/:id/balance — mirrors
 * paymentsService.getInvoiceBalanceInternal EXACTLY, including the cents math:
 * replica Payment.amount is Int CENTS (server shape; the optimistic offline cash
 * row is also stamped in cents), Invoice.amount/taxAmount are Float DOLLARS.
 * balanceDue deducts succeeded only (processing shown separately), like REST.
 */
export async function readInvoiceBalance(invoiceId: string): Promise<{
  total: number;
  taxAmount: number;
  totalWithTax: number;
  totalPaidCents: number;
  totalPaid: number;
  processingAmountCents: number;
  processingAmount: number;
  balanceDueCents: number;
  balanceDue: number;
  payments: any[];
  voidedPayments: any[];
}> {
  const invoice = (await db.invoices.get(invoiceId)) ?? ({} as Row);
  const rows = (await db.payments.toArray()).filter((p) => p.invoiceId === invoiceId);
  // Mirrors the REST contract: `payments` is succeeded/processing only (the
  // shipped mobile shape); voided history rides the additive voidedPayments
  // key and never counts toward totals.
  const all = rows.filter((p) => p.status === 'succeeded' || p.status === 'processing');
  const voided = rows.filter((p) => p.status === 'voided');
  const total = typeof invoice.amount === 'number' ? invoice.amount : 0;
  const taxAmount = Number(invoice.taxAmount ?? 0);
  const totalWithTax = total + taxAmount;
  const totalWithTaxCents = toCents(totalWithTax);
  const totalPaidCents = all
    .filter((p) => p.status === 'succeeded')
    .reduce((sum, p) => sum + (typeof p.amount === 'number' ? p.amount : 0), 0);
  const processingCents = all
    .filter((p) => p.status === 'processing')
    .reduce((sum, p) => sum + (typeof p.amount === 'number' ? p.amount : 0), 0);
  const balanceDueCents = Math.max(0, totalWithTaxCents - totalPaidCents);
  return {
    total,
    taxAmount,
    totalWithTax,
    totalPaidCents,
    totalPaid: totalPaidCents / 100,
    processingAmountCents: processingCents,
    processingAmount: processingCents / 100,
    balanceDueCents,
    balanceDue: balanceDueCents / 100,
    payments: all
      .sort((a, b) => `${a.createdAt ?? ''}`.localeCompare(`${b.createdAt ?? ''}`))
      .map((p) => ({
        id: p.id,
        amount: (typeof p.amount === 'number' ? p.amount : 0) / 100,
        amountCents: p.amount,
        status: p.status,
        paymentType: p.paymentType || p.paymentMethod,
        paymentMethod: p.paymentMethod,
        referenceNote: p.referenceNote,
        paymentDate: p.paymentDate || p.processedAt || p.createdAt,
        cardBrand: p.cardBrand,
        cardLast4: p.cardLast4,
        stripePaymentIntentId: p.stripePaymentIntentId,
        voidReason: p.voidReason,
        payerName: p.payerName,
        createdAt: p.createdAt,
      })),
    voidedPayments: voided
      .sort((a, b) => `${a.createdAt ?? ''}`.localeCompare(`${b.createdAt ?? ''}`))
      .map((p) => ({
        id: p.id,
        amount: (typeof p.amount === 'number' ? p.amount : 0) / 100,
        amountCents: p.amount,
        status: p.status,
        paymentType: p.paymentType || p.paymentMethod,
        paymentMethod: p.paymentMethod,
        referenceNote: p.referenceNote,
        paymentDate: p.paymentDate || p.processedAt || p.createdAt,
        cardBrand: p.cardBrand,
        cardLast4: p.cardLast4,
        stripePaymentIntentId: p.stripePaymentIntentId,
        voidReason: p.voidReason,
        payerName: p.payerName,
        voidedAt: p.voidedAt,
        createdAt: p.createdAt,
      })),
  };
}

// ─── Reports aggregations (offline) ───────────────────────────────────────────
// Recompute the /reports/* server endpoints from the replica so the Reports page
// renders offline. Each MIRRORS backend ReportsService EXACTLY (the 'paid' status
// literal, the color maps, the date buckets, the <=10/<=25 thresholds) so the
// offline numbers + colors match online. All source tables are fully replicated.

// reports.service getAppointmentTypes: its inactive statuses and its rank palette.
const INACTIVE_APPOINTMENT_STATUSES = ['cancelled', 'canceled', 'no-show', 'no_show'];
const TYPE_PALETTE = ['#8B4513', '#27AE60', '#D35400', '#F39C12', '#C0392B', '#7D3C98', '#2C3E50', '#16A085', '#8E44AD', '#2980B9'];
const BREED_COLORS: Record<string, string> = {
  Thoroughbred: '#4CAF50',
  'Quarter Horse': '#2196F3',
  Arabian: '#FFC107',
  Other: '#9E9E9E',
};

const isPaidInvoice = (inv: Row): boolean => inv.status === 'paid';
const invoiceTotal = (inv: Row): number =>
  (typeof inv.amount === 'number' ? inv.amount : 0) +
  (typeof inv.taxAmount === 'number' ? inv.taxAmount : 0);
const within30DaysPast = (cutoff: number) => (a: Row): boolean => {
  const t = new Date(a.date as string).getTime();
  return !Number.isNaN(t) && t >= cutoff;
};

/** /reports/revenue — paid-invoice totals (amount+tax) for the last 5 months. */
export async function readRevenueByMonth(): Promise<{ month: string; amount: number }[]> {
  const invoices = (await db.invoices.toArray()).filter(isPaidInvoice);
  const out: { month: string; amount: number }[] = [];
  for (let i = 0; i < 5; i++) {
    const date = subMonths(new Date(), i);
    const start = startOfMonth(date).getTime();
    const end = endOfMonth(date).getTime();
    const inBucket = (v: any): boolean => {
      const t = v ? new Date(v).getTime() : NaN;
      return !Number.isNaN(t) && t >= start && t <= end;
    };
    const amount = invoices
      .filter((inv) => inBucket(inv.issuedAt) || inBucket(inv.paidAt))
      .reduce((sum, inv) => sum + invoiceTotal(inv), 0);
    out.unshift({ month: format(date, 'MMM'), amount });
  }
  return out;
}

/**
 * /reports/appointments with no range: the last 30 days by type, the server's rule. A type's other
 * spellings and aliases count under the clinic's type name, in its Schedule colour.
 */
export async function readAppointmentTypes(): Promise<{ type: string; count: number; color: string }[]> {
  const cutoff = Date.now() - 30 * 86_400_000;
  const appts = (await db.appointments.toArray())
    .filter(within30DaysPast(cutoff))
    .filter((a) => !INACTIVE_APPOINTMENT_STATUSES.includes(`${a.status ?? ''}`));
  const catalog = (await db.serviceCatalog.toArray()).filter((item) => item.clinicId && item.category === 'appointment');
  const norm = (t: string) => t.trim().replace(/\s+/g, ' ').toLowerCase();
  const known = new Map<string, { name: string; color: string | null }>();
  // Names first, then aliases that name nothing else (the server's rule).
  const entryOf = (item: Row) => ({ name: `${item.name}`, color: (item.color as string | null) ?? null });
  for (const item of catalog) known.set(norm(`${item.name}`), entryOf(item));
  for (const item of catalog) {
    for (const alias of Array.isArray(item.aliases) ? (item.aliases as string[]) : []) {
      if (!known.has(norm(alias))) known.set(norm(alias), entryOf(item));
    }
  }
  const rows = new Map<string, { type: string; count: number; color: string | null }>();
  for (const a of appts) {
    const raw = `${a.type ?? ''}`;
    const entry = known.get(norm(raw));
    const key = entry ? norm(entry.name) : norm(raw);
    const row = rows.get(key) ?? { type: entry?.name ?? raw.trim().replace(/\s+/g, ' '), count: 0, color: entry?.color ?? null };
    row.count += 1;
    rows.set(key, row);
  }
  return Array.from(rows.values())
    .sort((a, b) => b.count - a.count)
    .map((row, i) => ({ type: row.type, count: row.count, color: row.color ?? TYPE_PALETTE[i % TYPE_PALETTE.length] }));
}

/** /reports/breeds — top-3 breed distribution (% of all horses; server color map). */
export async function readBreedDistribution(): Promise<{ breed: string; percentage: number; color: string }[]> {
  const horses = await db.horses.toArray();
  const total = horses.length;
  if (total === 0) return [];
  const counts: Record<string, number> = {};
  for (const h of horses) {
    const breed = `${h.breed ?? ''}`;
    counts[breed] = (counts[breed] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([breed, count]) => ({
      breed,
      percentage: Math.round((count / total) * 100),
      color: BREED_COLORS[breed] || BREED_COLORS.Other,
    }))
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 3);
}

/** /reports/vaccinations — active vaccinations due today through 30 days out in the clinic's calendar (+ owner). */
export async function readUpcomingVaccinations(
  timeZone: string,
  now: number = Date.now(),
): Promise<{ horseName: string; ownerName: string; vaccineType: string; dueDate: string }[]> {
  const due = (await db.vaccinations.toArray())
    .filter((v) => v.isArchived === false && v.nextDueDate)
    .filter((v) => {
      // The server's window in days (DATE-KINDS §2.9); instants dropped a vaccine on its own due day.
      const days = calendarDaysUntil(v.nextDueDate as string, timeZone, now);
      return days !== null && days >= 0 && days <= 30;
    })
    .sort((a, b) => `${a.nextDueDate}`.localeCompare(`${b.nextDueDate}`));
  const out: { horseName: string; ownerName: string; vaccineType: string; dueDate: string }[] = [];
  for (const v of due) {
    const horse = await db.horses.get(v.horseId as string);
    const owners = horse && Array.isArray(horse.owners) ? horse.owners : [];
    out.push({
      horseName: `${horse?.name ?? 'Unknown'}`,
      ownerName: (owners[0] as any)?.name || 'Unknown',
      vaccineType: `${v.vaccineName || 'General Vaccination'}`,
      // CALENDAR day (Vaccination.nextDueDate, DATE-KINDS §1.1): parse the date
      // PART, never re-zone. Re-wrapping the ISO in a Date and formatting it —
      // the idiom that stood here — read the UTC-midnight anchor in the vet's
      // browser zone and printed the PREVIOUS day in every US zone. That is the
      // rollback DATE-KINDS §1.6 names as the one defect on the offline surface;
      // the online twin (reports.service.ts) escapes it only because Render
      // runs UTC. ⚠ Do NOT "improve" this to a clinic-zone render — applying a
      // zone to a calendar day is the INVERSE trap (§3.2).
      dueDate: formatCalendarDate(v.nextDueDate as string, 'MMMM d, yyyy'),
    });
  }
  return out;
}

/**
 * /reports/inventory — mirrors ReportsService.getInventoryStatus: each item is
 * judged against ITS OWN thresholds through the one low-stock predicate (no
 * fabricated default). Zero on-hand is always Low Stock (minRequired 0); an
 * in-stock item with no threshold is not reported; Warning = within 1.5x of
 * the configured bar.
 */
export async function readInventoryStatus(): Promise<
  { name: string; category: string; status: string; currentStock: number; minRequired: number }[]
> {
  return (await db.inventoryItems.toArray())
    .filter((i) => typeof i.quantity === 'number')
    .sort((a, b) => (a.quantity as number) - (b.quantity as number))
    .map((i) => {
      const quantity = i.quantity as number;
      const item = { quantity, lowStockAlert: i.lowStockAlert as number | null | undefined, reorderPoint: i.reorderPoint as number | null | undefined };
      const threshold = lowStockThresholdOf(item);
      const status = isLowStockItem(item)
        ? 'Low Stock'
        : threshold !== null && quantity <= threshold * 1.5
          ? 'Warning'
          : 'OK';
      return {
        name: `${i.name ?? ''}`,
        category: `${i.category ?? ''}`,
        status,
        currentStock: quantity,
        minRequired: threshold ?? 0,
      };
    })
    .filter((row) => row.status !== 'OK');
}

/**
 * /billing/stats/horse/:horseId — per-horse invoice summary from the replica.
 *
 * Mirrors `billingService.getHorseInvoiceStats` AFTER A5c: membership is
 * OPEN_INVOICE_STATUSES (so `partially_paid` counts), the balance is netted in
 * CENTS against succeeded + processing payments and clamped PER INVOICE, and the
 * in-flight half is reported on its own. Before A5c both sides summed GROSS over
 * `pending`/`overdue`; the online figure moved, so this one has to move with it
 * or the patient card reads two balances off the same replica.
 *
 * Payments ARE replicated, so unlike the clinic tiles' PAST-DUE split (which needs
 * the clinic's calendar day and is suppressed offline) there is nothing to guess
 * here — online and offline agree exactly.
 *
 * (Per-CLIENT stats are intentionally NOT offline — the backend resolves owned
 * horses via the HorseClient table, which isn't replicated, so an offline number
 * would undercount legacy invoices. Left online-only.)
 *
 * ⚠ The invoice LIST rows have no offline `processingAmount` (INVR145): hydrating
 * one per row means a second replica scan per page, and `readInvoiceStats` already
 * costs eight (INVR120). Offline the row caption is simply absent — the money is
 * not misstated, it is unsaid.
 */
export async function readHorseInvoiceStats(horseId: string): Promise<{
  balance: number;
  openInvoiceCount: number;
  lastInvoiceDate: string | null;
  totalInvoiceCount: number;
  processingAmount: number;
}> {
  const all = (await db.invoices.toArray()).filter((i) => i.horseId === horseId);
  const open = all.filter((i) =>
    (OPEN_INVOICE_STATUSES as readonly string[]).includes(`${i.status}`),
  );
  const openIds = new Set(open.map((i) => `${i.id}`));

  const paidCentsBy = new Map<string, number>();
  const processingCentsBy = new Map<string, number>();
  for (const p of await db.payments.toArray()) {
    const key = `${p.invoiceId}`;
    if (!openIds.has(key)) continue;
    // Same filter as readInvoiceBalance / readInvoiceStats / the server's
    // AR_PAYMENTS_INCLUDE: failed / refunded / voided money never counts.
    if (p.status !== 'succeeded' && p.status !== 'processing') continue;
    const cents = typeof p.amount === 'number' ? p.amount : 0;
    paidCentsBy.set(key, (paidCentsBy.get(key) ?? 0) + cents);
    if (p.status === 'processing') {
      processingCentsBy.set(key, (processingCentsBy.get(key) ?? 0) + cents);
    }
  }

  const lastInvoiceDate = all.reduce<string | null>((acc, i) => {
    const d = i.issuedAt ? `${i.issuedAt}` : null;
    return d && (!acc || d > acc) ? d : acc;
  }, null);

  let balanceCents = 0;
  let inFlightCents = 0;
  for (const i of open) {
    const key = `${i.id}`;
    // Clamped PER INVOICE: an overpaid row contributes 0, never a credit against
    // another row's debt (INV-34).
    balanceCents += Math.max(0, toCents(invoiceTotal(i)) - (paidCentsBy.get(key) ?? 0));
    inFlightCents += processingCentsBy.get(key) ?? 0;
  }

  return {
    balance: fromCents(balanceCents),
    // Every open row in scope, at $0 owed too — it labels the Invoices tab's list.
    openInvoiceCount: open.length,
    lastInvoiceDate,
    totalInvoiceCount: all.length,
    processingAmount: fromCents(inFlightCents),
  };
}

/** /reports/stats — summary KPIs (horses, last-30d appts, distinct breeds, paid revenue). */
export async function readGeneralStats(): Promise<{
  totalHorses: number;
  totalAppointments: number;
  breedCount: number;
  totalRevenue: number;
}> {
  const [horses, appts, invoices] = await Promise.all([
    db.horses.toArray(),
    db.appointments.toArray(),
    db.invoices.toArray(),
  ]);
  const cutoff = Date.now() - 30 * 86_400_000;
  return {
    totalHorses: horses.length,
    totalAppointments: appts.filter(within30DaysPast(cutoff)).length,
    breedCount: new Set(horses.map((h) => h.breed)).size,
    totalRevenue: invoices.filter(isPaidInvoice).reduce((sum, inv) => sum + invoiceTotal(inv), 0),
  };
}

export async function readHorseCount(): Promise<number> {
  return db.horses.count();
}

/** Local equivalent of the inventory list (filter by search/category in memory). */
export async function readInventoryItems(p: { search?: string; category?: string } = {}): Promise<Row[]> {
  let rows = await db.inventoryItems.toArray();
  if (p.search) {
    const q = p.search.toLowerCase();
    rows = rows.filter((i) => `${i.name ?? ''}`.toLowerCase().includes(q));
  }
  if (p.category && p.category !== 'ALL') rows = rows.filter((i) => i.category === p.category);
  return rows.sort((a, b) => `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`));
}

export async function readInventoryStats(): Promise<{ total: number; lowStock: number; byCategory: Record<string, number> }> {
  const rows = await db.inventoryItems.toArray();
  const byCategory: Record<string, number> = {};
  let lowStock = 0;
  for (const i of rows) {
    const cat = `${i.category ?? 'uncategorized'}`;
    byCategory[cat] = (byCategory[cat] ?? 0) + 1;
    // Match inventory.service.getInventoryStats through the shared predicate
    // (utils/lowStock mirrors backend low-stock.ts): on-hand <= 0 is always low
    // stock; `lowStockAlert` / `reorderPoint` thresholds are inclusive; the
    // persisted needsPurchase flag also counts.
    if (
      isLowStockItem({
        quantity: i.quantity,
        lowStockAlert: i.lowStockAlert,
        reorderPoint: i.reorderPoint,
        needsPurchase: i.needsPurchase,
      })
    ) {
      lowStock += 1;
    }
  }
  return { total: rows.length, lowStock, byCategory };
}

export interface InvoicePageParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  horseId?: string;
  clientId?: string;
  /**
   * The Billing page's "Date Range" window, as ISO instants — the same values it
   * sends the server (`findAllPaginated`'s `issuedAt` gte/lte). Applied here too,
   * or the SAME control would scope the list online and silently not offline.
   *
   * The page sends `startDate` ONLY (a browser-clock `endDate` bounding a
   * server-generated `issuedAt` could hide a just-created invoice); `endDate`
   * stays accepted because this reader mirrors the server's parameters, and an
   * absent one means "no upper bound".
   */
  startDate?: string;
  endDate?: string;
}

/**
 * Shared invoice display context: horse/client maps for hydration + succeeded
 * payment cents per invoice for the DERIVED display status. The /sync feed
 * stores scalar-only invoice rows, but the Billing list/detail hard-deref
 * `invoice.horse.name` / `invoice.client` (the REST include shape) — a bare
 * replica row white-screened the app via the root ErrorBoundary.
 */
async function invoiceDisplayContext() {
  const [horses, clients, payments] = await Promise.all([
    db.horses.toArray(),
    db.clients.toArray(),
    db.payments.toArray(),
  ]);
  const horseById = new Map(horses.map((h) => [h.id, h]));
  const clientById = new Map(clients.map((c) => [c.id, c]));
  const succeededCentsByInvoice = new Map<string, number>();
  for (const p of payments) {
    if (p.status === 'succeeded' && p.invoiceId) {
      succeededCentsByInvoice.set(
        p.invoiceId as string,
        (succeededCentsByInvoice.get(p.invoiceId as string) ?? 0) +
          (typeof p.amount === 'number' ? p.amount : 0),
      );
    }
  }
  return { horseById, clientById, succeededCentsByInvoice };
}

function hydrateInvoice(row: Row, ctx: Awaited<ReturnType<typeof invoiceDisplayContext>>): Row {
  const horse = row.horseId ? ctx.horseById.get(row.horseId as string) : undefined;
  const client = row.clientId ? ctx.clientById.get(row.clientId as string) : undefined;
  // DERIVED display status: an offline cash payment covering the full total
  // shows the invoice as paid. Derived (never stored), so discarding a failed
  // payment op removes its optimistic row and the display self-heals — no
  // phantom-'paid' stuck in the replica. Server truth overwrites on sync.
  let status = row.status;
  if (status !== 'paid' && status !== 'cancelled') {
    const totalCents = toCents(
      (typeof row.amount === 'number' ? row.amount : 0) + Number(row.taxAmount ?? 0),
    );
    const paidCents = ctx.succeededCentsByInvoice.get(row.id) ?? 0;
    if (totalCents > 0 && paidCents >= totalCents) status = 'paid';
  }
  return {
    ...row,
    status,
    // Default shape keeps temp-id optimistic invoices renderable (no crash).
    horse: horse ?? { name: '', owners: [] },
    ...(client ? { client } : {}),
  };
}

/** Local equivalent of billingApi.listInvoicesPaginated (filter/sort/paginate). */
export async function readInvoicesPage(p: InvoicePageParams = {}): Promise<Paginated<Row>> {
  const ctx = await invoiceDisplayContext();
  // Hydrate BEFORE filtering so the derived status drives the tab filter and
  // search can match the horse name (online parity: server matches
  // invoiceNumber OR horse name).
  let rows = (await db.invoices.toArray()).map((i) => hydrateInvoice(i, ctx));
  // Same rule as the server's findAllPaginated: Pending also holds partly paid invoices.
  const tabStatuses = p.status === 'pending' ? ['pending', 'partially_paid'] : [p.status];
  if (p.status && p.status !== 'ALL') rows = rows.filter((i) => tabStatuses.includes(i.status));
  if (p.horseId) rows = rows.filter((i) => i.horseId === p.horseId);
  if (p.startDate || p.endDate) {
    // The server filters `issuedAt` (gte/lte). An optimistic offline row carries
    // its own `issuedAt` (the queued create's `extra`), and `createdAt` is the
    // same fallback this reader's sort uses; a row with NEITHER is kept rather
    // than silently dropped out of every window.
    const from = p.startDate ? Date.parse(p.startDate) : -Infinity;
    const to = p.endDate ? Date.parse(p.endDate) : Infinity;
    rows = rows.filter((i) => {
      const at = Date.parse(`${i.issuedAt ?? i.createdAt ?? ''}`);
      return Number.isNaN(at) || (at >= from && at <= to);
    });
  }
  // ⚠ DIVERGES from the server ON PURPOSE (A1, 2026-09-09). Online, `clientId`
  // means MEMBERSHIP: a legacy invoice with no clientId whose horse this client
  // OWNS is on their list too (billing/client-invoice-scope.ts). The replica
  // cannot mirror that — /sync carries no HorseClient rows (PULL_ENTITIES has
  // no horseClient) and only Horse.owners NAME json, so a local join would be
  // role-blind and wrong in a different direction. Offline keeps clientId
  // equality and can therefore UNDERCOUNT legacy invoices — same reason
  // readHorseInvoiceStats' docblock keeps per-CLIENT stats online-only.
  if (p.clientId) rows = rows.filter((i) => i.clientId === p.clientId);
  if (p.search) {
    const q = p.search.toLowerCase();
    rows = rows.filter(
      (i) =>
        `${i.invoiceNumber ?? i.id ?? ''}`.toLowerCase().includes(q) ||
        `${(i.horse as any)?.name ?? ''}`.toLowerCase().includes(q),
    );
  }
  rows.sort((a, b) => `${b.issuedAt ?? b.createdAt ?? ''}`.localeCompare(`${a.issuedAt ?? a.createdAt ?? ''}`));
  return paginate(rows, p.page ?? 1, p.limit ?? 50);
}

export async function readInvoice(id: string): Promise<Row | undefined> {
  const row = await db.invoices.get(id);
  if (!row) return row;
  return hydrateInvoice(row, await invoiceDisplayContext());
}

/**
 * Statuses that are OPEN RECEIVABLES — the replica mirror of the backend's
 * `modules/billing/ar.ts` OPEN_AR_STATUSES (INV-34). `partially_paid` is IN the
 * set: it is the status a half-paid invoice carries, and leaving it out is how
 * the Billing tiles showed a half-paid invoice in no bucket at all.
 */
export const OPEN_INVOICE_STATUSES = ['pending', 'overdue', 'partially_paid'] as const;

/**
 * The replica's half of `GET /billing/stats/clinic`'s per-status aggregate.
 *
 * ONE rule decides gross vs net, so no caller has to remember a flag: a row in
 * OPEN_INVOICE_STATUSES reports what is still OWED, any other row (`paid`,
 * `sent`, `cancelled`) reports what was BILLED — which is what the revenue and
 * average tiles mean, and netting those would zero them. That is exactly the
 * split `billing.service.getClinicInvoiceStats` makes: its open buckets are
 * netted and its revenue `groupBy` is not.
 *
 * Owed is netted in CENTS (Payment.amount is Int cents, Invoice.amount/
 * taxAmount are Float dollars — ADR-0029) and clamped at zero per invoice, so
 * an over-paid row can never subtract from another row's balance.
 *
 * `processing` is the in-flight ACH total over the matched OPEN rows, ALREADY
 * deducted from `total`; it backs the "+ $X processing (ACH)" tile caption.
 *
 * TWO COUNTS, because the server publishes two and they are not the same
 * number. `count` is the rows in the matched set — the replica half of the
 * per-status `_count._all` that feeds `totalCount`, `paidCount30Days` and the
 * `averageInvoice` denominator, where the server counts EVERY row. `owedCount`
 * is the narrower figure its open plane publishes as `openInvoiceCount`:
 * `COUNT(*) FILTER (WHERE sub.owed > 0)`, over the OPEN rows only. They differ
 * by exactly the open rows that owe nothing — reachable offline TODAY, because
 * a full cash payment recorded offline stamps a succeeded payment row while the
 * replica invoice keeps `status: 'pending'` until it syncs. ⚠ A tile that shows
 * an OPEN AMOUNT must put `owedCount` beside it (`$0 / 0 pending` — the pair
 * the same clinic reads online), never `count` (`$0 / 1 pending`, a netted
 * amount over an un-netted count); a tile that counts INVOICES uses `count`.
 *
 * ⚠ PAST DUE is deliberately NOT split here: the server derives it from the
 * CLINIC's calendar day, which the replica cannot cheaply reproduce, so the
 * offline arms report the whole open balance as Outstanding and suppress the
 * past-due figure rather than guessing it.
 */
export async function readInvoiceStats(
  status?: string | string[],
  opts?: { paidSince?: Date },
): Promise<{ count: number; total: number; processing: number; owedCount: number }> {
  let rows = await db.invoices.toArray();
  if (status && status !== 'ALL') {
    const wanted = Array.isArray(status) ? status : [status];
    rows = rows.filter((i) => wanted.includes(`${i.status}`));
  }
  if (opts?.paidSince) {
    const since = opts.paidSince.getTime();
    rows = rows.filter((i) => {
      const t = i.paidAt ? new Date(i.paidAt as string).getTime() : NaN;
      return !Number.isNaN(t) && t >= since;
    });
  }

  const matchedIds = new Set(rows.map((i) => `${i.id}`));
  const paidCentsBy = new Map<string, number>();
  const processingCentsBy = new Map<string, number>();
  for (const p of await db.payments.toArray()) {
    const key = `${p.invoiceId}`;
    if (!matchedIds.has(key)) continue;
    // Same filter as readInvoiceBalance / the server's AR_PAYMENTS_INCLUDE:
    // failed / refunded / voided money never counts.
    if (p.status !== 'succeeded' && p.status !== 'processing') continue;
    const cents = typeof p.amount === 'number' ? p.amount : 0;
    paidCentsBy.set(key, (paidCentsBy.get(key) ?? 0) + cents);
    if (p.status === 'processing') {
      processingCentsBy.set(key, (processingCentsBy.get(key) ?? 0) + cents);
    }
  }

  const openSet = OPEN_INVOICE_STATUSES as readonly string[];
  let totalCents = 0;
  let processingCents = 0;
  let owedCount = 0;
  for (const i of rows) {
    // amount is the pre-tax subtotal; REST stats include tax, so mirror it
    // (same amount+taxAmount derivation as readHorseInvoiceStats).
    const grossCents = toCents(invoiceTotal(i));
    if (openSet.includes(`${i.status}`)) {
      const key = `${i.id}`;
      const owedCents = Math.max(0, grossCents - (paidCentsBy.get(key) ?? 0));
      totalCents += owedCents;
      processingCents += processingCentsBy.get(key) ?? 0;
      // The server's COUNT(*) FILTER (WHERE sub.owed > 0) — see the docblock.
      if (owedCents > 0) owedCount += 1;
    } else {
      totalCents += grossCents;
    }
  }

  return {
    count: rows.length,
    total: fromCents(totalCents),
    processing: fromCents(processingCents),
    owedCount,
  };
}

/**
 * Vet calendar blocks for the offline calendar. Clinic-scoped table (no vetId
 * index) → filter by vet + date range in memory (low volume per clinic).
 */
export async function readTimeBlocks(p: { dateFrom?: string; dateTo?: string; vetId?: string } = {}): Promise<Row[]> {
  const from = p.dateFrom ? new Date(p.dateFrom).getTime() : -Infinity;
  const to = p.dateTo ? new Date(p.dateTo).getTime() : Infinity;
  let rows = await db.timeBlocks.toArray();
  // Clinic-wide closures (vetId null, ADR-0028) apply to every vet — a vetId
  // filter must keep them or the offline calendar hides a closed clinic.
  if (p.vetId) rows = rows.filter((b) => b.vetId === p.vetId || b.vetId == null);
  rows = rows.filter((b) => {
    const t = new Date(b.startTime as string).getTime();
    return !Number.isNaN(t) && t >= from && t <= to;
  });
  return rows.sort((a, b) => `${a.startTime ?? ''}`.localeCompare(`${b.startTime ?? ''}`));
}

export async function readDistinctLocations(): Promise<string[]> {
  const rows = await db.horses.toArray();
  return Array.from(new Set(rows.map((h) => h.location).filter(Boolean))) as string[];
}

/**
 * Local equivalent of GET /users/me — the replica holds the clinic's users.
 * Mirrors the REST resolution order: id match first, then firebaseUid/email
 * fallback — legacy/invited rows can have User.id !== Firebase uid, and the
 * backend resolves exactly those by email.
 */
export async function readCurrentUser(uid?: string, email?: string): Promise<Row | undefined> {
  if (!uid && !email) return undefined;
  const byId = uid ? await db.users.get(uid) : undefined;
  if (byId) return byId;
  const lcEmail = email?.toLowerCase();
  return db.users
    .filter(
      (u) =>
        (!!uid && u.firebaseUid === uid) ||
        (!!lcEmail && `${u.email ?? ''}`.toLowerCase() === lcEmail),
    )
    .first();
}

/**
 * Local equivalent of GET /horses/flagged (InsightsPanel "Need attention").
 * Mirrors HorsesService.findFlagged: non-deceased horses with at least one
 * isCritical medical alert, slim payload + criticalAlerts, name-sorted.
 */
export async function readFlaggedHorses(): Promise<Row[]> {
  const rows = await db.horses.toArray();
  return rows
    .filter((h) => h.status !== 'DECEASED')
    .map((h) => {
      const alerts = Array.isArray(h.medicalAlerts) ? (h.medicalAlerts as any[]) : [];
      const criticalAlerts = alerts.filter(
        (a) => a && typeof a === 'object' && a.isCritical === true,
      );
      return {
        id: h.id,
        name: h.name,
        patientId: h.patientId,
        status: h.status,
        medicalAlerts: h.medicalAlerts,
        criticalAlerts,
      } as Row;
    })
    .filter((h) => (h.criticalAlerts as any[]).length > 0)
    .sort((a, b) => `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`));
}

/**
 * The GLOBAL starter-catalog template rows (clinicId = null) — what the REST
 * /service-catalog/templates endpoint returns. Kept separate from
 * readServiceCatalog, which mirrors the clinic-only list endpoint.
 */
export async function readServiceCatalogTemplates(): Promise<Row[]> {
  const rows = (await db.serviceCatalog.toArray()).filter(
    (i) => i.clinicId == null && i.isActive !== false,
  );
  // REST orders by category then name — keep the offline dialog identical.
  return rows.sort(
    (a, b) =>
      `${a.category ?? ''}`.localeCompare(`${b.category ?? ''}`) ||
      `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`),
  );
}

/**
 * StockLocations for offline reads (Phase 3b trucks) — mirrors REST
 * GET /inventory/locations (InventoryService.listLocations) EXACTLY:
 * clinic-owned rows, default warehouse first (`isDefault desc`), then
 * alphabetical, so the UI can always show the warehouse up top offline too.
 */
export async function readStockLocations(): Promise<Row[]> {
  const rows = await db.stockLocations.toArray();
  return rows.sort((a, b) => {
    const defaultDiff = (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
    if (defaultDiff !== 0) return defaultDiff;
    return `${a.name ?? ''}`.localeCompare(`${b.name ?? ''}`);
  });
}

function paginate<T>(rows: T[], page: number, limit: number): Paginated<T> {
  const total = rows.length;
  const start = (page - 1) * limit;
  return {
    data: rows.slice(start, start + limit),
    total,
    page,
    limit,
    totalPages: Math.max(1, Math.ceil(total / limit)),
  };
}
